// The wizard's state: the draft and the step, kept in sessionStorage so a trip to link this
// browser or add a secret elsewhere does not lose the work. Moving forward never passes a step
// that is not ready; entering the secrets step binds the obvious secrets, and entering the
// review suggests a name until the user writes their own.
import { useCallback, useEffect, useState } from "react";

import { type Credential, type Draft, EMPTY_DRAFT, prefill, reachable, STEPS, suggestedName } from "./draft";

const KEY = "ocs.newTaskGroup";
const ACCESS = STEPS.indexOf("access");
const REVIEW = STEPS.indexOf("review");

interface State {
  draft: Draft;
  step: number;
  /** The user typed a name: stop suggesting one. */
  named: boolean;
}

const INITIAL: State = { draft: EMPTY_DRAFT, step: 0, named: false };

function restore(): State {
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Partial<State> | null;
    if (!saved || typeof saved !== "object" || !saved.draft) return INITIAL;
    const draft = { ...EMPTY_DRAFT, ...saved.draft };
    return { draft, step: Math.min(Number(saved.step) || 0, reachable(draft)), named: saved.named === true };
  } catch {
    return INITIAL;
  }
}

function save(state: State | null) {
  try {
    if (state) sessionStorage.setItem(KEY, JSON.stringify(state));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Storage can be blocked (private mode); the wizard still works, it just forgets on reload.
  }
}

function enter(s: State, to: number, credentials: Credential[]): State {
  let draft = to >= ACCESS ? prefill(s.draft, credentials) : s.draft;
  if (to > reachable(draft)) return s;
  if (to === REVIEW && !s.named) draft = { ...draft, name: suggestedName(draft) };
  return { ...s, draft, step: to };
}

export function useWizard(credentials: Credential[]) {
  const [state, setState] = useState<State>(restore);
  useEffect(() => save(state === INITIAL ? null : state), [state]);

  const change = useCallback((patch: Partial<Draft>) => setState((s) => ({ ...s, draft: { ...s.draft, ...patch }, named: s.named || "name" in patch })), []);
  const go = useCallback((to: number) => setState((s) => enter(s, to, credentials)), [credentials]);
  const finish = useCallback(() => {
    save(null);
    setState(INITIAL);
  }, []);
  // Secrets that arrive after the step was entered (loaded late, or just added) bind too.
  const draft = state.step >= ACCESS ? prefill(state.draft, credentials) : state.draft;
  return { draft, step: state.step, change, go, finish };
}
